import type { ComponentType } from 'react';
import {
  Accessibility,
  Blocks,
  Building2,
  Database,
  KeyRound,
  LayoutList,
  ListChecks,
  Plug,
  Route,
  ScrollText,
  SlidersHorizontal,
  Tags,
  UserRound,
  Users,
  UsersRound,
} from 'lucide-react';
import type { PlatformRole } from '@/lib/api/contract';
import { PERMISO_CUENTAS, type Permiso } from '@/lib/catalogo-de-permisos';
import { ROLES_QUE_ADMINISTRAN } from '@/lib/equipo';
import { MODULES, type CapabilityId, type LeadFieldId } from '@/lib/modules';
import type { UserRole } from '@/types';

export type SettingsGroupId = 'personal' | 'organization' | 'data' | 'platform';

export interface SettingsSection {
  id: string;
  /** Route segment under `/settings`; identifiers stay English, labels do not. */
  path: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  group: SettingsGroupId;
  /** Section disappears from the nav and answers "sin acceso" without it. */
  capability?: CapabilityId;
  /**
   * Only Ribox staff see it —los DOS roles nuestros, super y soporte: configurar
   * es justamente lo que el soporte sí hace—. Not a capability, because a
   * capability is something an organisation *has*, and this is precisely the
   * screen that decides what it has, so it cannot be gated by its own output.
   */
  platformOnly?: boolean;
  /**
   * La sección se ve, pero **guardar es nuestro**.
   *
   * Es el segundo nivel, y existe porque el servidor tiene esa misma forma: leer
   * la configuración lo puede hacer cualquiera de la organización —saber qué
   * campos personalizados existe es parte de trabajar— y escribirla no. Marcar
   * estas secciones `platformOnly` las escondería, que es una respuesta
   * distinta y peor: el cliente dejaría de ver su propia configuración.
   *
   * La diferencia con no marcar nada es lo que importa: sin esto la pantalla
   * dibuja «Guardar» habilitado y el servidor contesta 403, o sea un control
   * que miente. Con esto dice, arriba y una sola vez, quién puede cambiarlo.
   */
  platformWrites?: boolean;
  /**
   * El permiso que decide si esta sección se GUARDA. No decide si se ve, salvo
   * con `soloQuienAdministra` (hoy, API), donde además la esconde.
   *
   * Es lo que impide quedar en los dos mundos: desde que el servidor cambió su
   * compuerta por `require_permission(config.usuarios)`, una pantalla que
   * siguiera preguntando por el rol dibujaría «Guardar» para quien la API
   * rechaza — el mismo defecto que `platformWrites` vino a cerrar del otro lado
   * el 2026-09-21.
   *
   * **Hasta el 2026-09-24 además ENSANCHABA la visibilidad**, para que una
   * sección `platformOnly` se le abriera al cliente que llevara el permiso. Esa
   * combinación tenía un solo caso —Ajustes → Usuarios— y el dueño la deshizo:
   * «everyone should be able to open usuarios». Sin ese caso, el ensanchamiento
   * era una rama sin usuario que sólo podía envejecer mal, así que se fue con
   * él. Vuelve en dos líneas el día que haya una sección nuestra que un permiso
   * del cliente tenga que abrir.
   */
  permiso?: Permiso;
  soloQuienAdministra?: boolean;
  /**
   * El campo del lead del que depende la sección, cuando depende de uno.
   *
   * Es la MISMA compuerta del §18 aplicada a Configuración, que era el último
   * lugar donde el catálogo de etiquetas seguía en pie: con `tags` apagado no
   * hay ninguna pantalla donde una etiqueta se vea, se ponga ni se filtre, y la
   * sección seguía listándolas, creándolas y borrándolas. Administrar un
   * vocabulario que el producto no dibuja en ninguna parte es peor que no
   * tenerlo: enseña palabras que después no aparecen.
   */
  leadField?: LeadFieldId;
}

export const SETTINGS_GROUPS: readonly { id: SettingsGroupId; label: string }[] = [
  { id: 'personal', label: 'Tu cuenta' },
  { id: 'organization', label: 'Organización' },
  { id: 'data', label: 'Datos' },
  { id: 'platform', label: 'Plataforma' },
];

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: 'account',
    path: 'account',
    label: 'Mi cuenta',
    description: 'Tu nombre, tu foto y el correo con el que entras al CRM.',
    icon: UserRound,
    group: 'personal',
  },
  {
    // Labelled «Empresa» and not «Organización» on purpose: the group above it
    // already says Organización, and a group heading repeated verbatim by its
    // first item reads like a rendering bug.
    id: 'organization',
    path: 'organization',
    label: 'Empresa',
    description: 'Nombre, logo, idioma, zona horaria, moneda y horario de atención.',
    icon: Building2,
    group: 'organization',
    // Se ve —es su propia empresa— pero `PATCH /api/v2/tenants/{id}` es de
    // plataforma desde el 2026-08-17, así que guardar no.
    platformWrites: true,
  },
  {
    // **LA VEN TODOS; LA GUARDAN LOS ADMINISTRADORES DEL CLIENTE.** Dueño,
    // 2026-09-24: «everyone should be able to open usuarios, and only admins
    // should be able to edit, delete and add users (company admins)».
    //
    // Es el final de un recorrido que conviene leer entero, porque las dos
    // paradas anteriores parecían razonables:
    //
    // 1. Hasta el 2026-09-22 era `platformOnly` a secas: dar de alta a alguien y
    //    darle un rol decide quién ve la cartera completa del negocio, así que lo
    //    hacíamos nosotros.
    // 2. El plan de cuentas se la abrió al Administrador del cliente con
    //    `permiso: config.usuarios`, y `platformOnly` se quedó al lado para que
    //    nuestro Soporte —que no lleva el permiso— pudiera seguir MIRANDO.
    // 3. Un rato existió además una capacidad `settings.users` que la apagaba
    //    por organización, leyendo la apertura del punto 2 como una fuga.
    //
    // Las tres escondían la pantalla de quien no la guarda, y eso es lo que el
    // dueño corrigió: mirar quién entra al CRM de tu empresa es parte de
    // trabajar ahí. Así que la sección no declara ni capacidad ni `platformOnly`
    // — `seccionVisible` contesta que sí para cualquiera— y conserva el permiso,
    // que es lo único que `seccionEditable` mira: sin `config.usuarios` la lista
    // se ve entera y no hay un solo control que escriba (`users-section.tsx`
    // esconde «Crear cuenta», las acciones de fila y los diálogos, y pone
    // `ReadOnlyNotice` en su lugar). Es la misma respuesta que da el servidor:
    // `list_users` no pide permiso y `users.py::_require_write` pide éste.
    id: 'users',
    path: 'users',
    label: 'Usuarios',
    description: 'Quién entra al CRM, con qué rol, con qué permisos y en qué equipos.',
    icon: Users,
    group: 'organization',
    permiso: PERMISO_CUENTAS,
  },
  {
    // **Ya hay backend** (`/api/v2/tenants/{t}/teams`), así que la marca dejó de
    // ser un parche y pasó a ser lo que dice: esto es nuestro.
    //
    // Estuvo escondida porque `api.teams` vivía en el simulador y lo que la
    // sección creaba y borraba se perdía al recargar. Hoy se guarda, y sigue
    // escondida por la razón de su vecina Usuarios: armar equipos es repartir
    // quién ve qué cartera —el selector de alcance del Tablero lee esta misma
    // lista— y eso es configuración (docs/DECISIONES.md §8). El servidor dice lo
    // mismo desde el otro lado: leer la lista lo puede hacer cualquiera de la
    // organización, escribirla pide `platform_admin`.
    id: 'teams',
    path: 'teams',
    label: 'Equipos',
    description: 'Agrupa a tu gente y define quién lidera cada equipo.',
    icon: UsersRound,
    group: 'organization',
    platformOnly: true,
  },
  {
    // Nuestro, no del cliente. Un embudo no es una preferencia: es el
    // contenedor donde viven físicamente sus leads, sus llamadas y su línea de
    // tiempo (docs/DECISIONES.md §1.6), y el CRM viejo está leyendo los mismos
    // documentos al mismo tiempo. Quitarle una etapa a un embudo lleno es
    // dejar registros apuntando a algo que ya no existe.
    id: 'pipelines',
    path: 'pipelines',
    label: 'Embudos',
    description: 'Etapas por embudo: orden, color, probabilidad y tipo.',
    icon: Route,
    group: 'data',
    platformOnly: true,
  },
  {
    id: 'fields',
    path: 'fields',
    label: 'Campos personalizados',
    description: 'Los datos propios de tu negocio, sin pedirle nada a desarrollo.',
    icon: SlidersHorizontal,
    group: 'data',
    capability: 'settings.custom_fields',
    // Los campos se leen para trabajar —las fichas los dibujan— pero definirlos
    // cambia la forma de los datos de todo el mundo, así que es nuestro.
    platformWrites: true,
  },
  {
    id: 'tags',
    path: 'tags',
    label: 'Etiquetas',
    leadField: 'tags',
    description: 'Catálogo compartido por leads, contactos, empresas y negocios.',
    icon: Tags,
    group: 'data',
  },
  {
    id: 'modules',
    path: 'modules',
    label: 'Módulos',
    description: 'Qué partes del CRM están encendidas para esta organización.',
    icon: Blocks,
    group: 'platform',
    platformOnly: true,
  },
  {
    // Vecina de Módulos y distinta de ella: aquélla dice qué **tiene** la
    // organización, ésta dice **dónde se ve**. Fueron lo mismo hasta el
    // 2026-08-18, y por eso esconder la pantalla de Bandeja le borraba al lead
    // su conversación de WhatsApp (docs/DECISIONES.md §10).
    id: 'views',
    path: 'views',
    label: 'Vistas',
    description: 'Qué pantallas aparecen en el menú y qué pestañas trae la ficha del lead.',
    icon: LayoutList,
    group: 'platform',
    platformOnly: true,
  },
  {
    // La tercera hermana de Módulos y Vistas, y la que faltaba: aquélla dice qué
    // **tiene** la organización, la otra **dónde se ve**, y ésta **qué datos
    // tiene una persona** para este cliente. Es el mecanismo aditivo de
    // `enabledLeadFields` (decisión del dueño, 2026-08-21), que se podía leer
    // desde entonces y sólo se podía escribir con un `curl` — el dueño pidió que
    // «origen» y «próximo seguimiento» se apagaran desde la pantalla, y hasta hoy
    // no había ninguna.
    //
    // No es «Campos personalizados»: aquélla inventa campos nuevos para este
    // negocio, ésta decide cuáles de los que el producto ya trae existen acá.
    id: 'lead-fields',
    path: 'lead-fields',
    label: 'Campos de una persona',
    description: 'Qué datos tiene un lead en esta organización: origen, próximo seguimiento, valor.',
    icon: ListChecks,
    group: 'platform',
    platformOnly: true,
  },
  {
    // La operación más peligrosa de toda la configuración, y por eso vive en su
    // propia sección en vez de escondida dentro de otra: le cambia la pantalla
    // principal a una organización entera, y hasta hoy se hacía editando
    // Firestore a mano —sin validación, sin registro de quién, con el documento
    // completo delante—.
    id: 'sources',
    path: 'sources',
    label: 'Fuentes de datos',
    description: 'De dónde lee cada pantalla: los embudos de siempre o el modelo de personas.',
    icon: Database,
    group: 'platform',
    platformOnly: true,
  },
  {
    // Personal, not organisational: contrast and theme live in this browser and
    // change nothing for the rest of the team.
    id: 'accessibility',
    path: 'accessibility',
    label: 'Accesibilidad',
    description: 'Contraste, tema y movimiento reducido.',
    icon: Accessibility,
    group: 'personal',
  },
  {
    // La pantalla que dice DE DÓNDE vienen los hechos que muestran las demás.
    //
    // No es un módulo: una conexión no es un lugar al que se entra, y no hay
    // nada que comprar para tenerla. Vive en Ajustes, al lado de «Accesos»,
    // porque las dos contestan la misma clase de pregunta — quién habla con
    // este CRM — desde los dos lados.
    //
    // No es `platformOnly`: la casilla la autoriza su dueño con su propia
    // cuenta, y nadie del cliente tendría que pedirnos permiso para conectar
    // —o desconectar— su propio correo.
    id: 'integrations',
    path: 'integrations',
    label: 'Integraciones',
    description:
      'Qué alimenta al CRM —correo, WhatsApp, calendario, Google Drive— y a dónde envía lo que registra: HubSpot.',
    icon: Plug,
    group: 'organization',
    capability: 'settings.integrations',
  },
  {
    id: 'api',
    path: 'api',
    label: 'API',
    description: 'Llaves para que tus sistemas carguen leads en Ribox, y cómo usar la API.',
    icon: KeyRound,
    group: 'organization',
    permiso: PERMISO_CUENTAS,
    soloQuienAdministra: true,
  },
  {
    id: 'audit',
    path: 'audit',
    label: 'Bitácora',
    description: 'Quién hizo qué, cuándo, y qué valor cambió.',
    icon: ScrollText,
    group: 'platform',
    capability: 'settings.audit_log',
    // Escondida porque no hay backend: `api.auditEvents` sigue en el simulador
    // y para una organización de verdad devuelve una lista vacía.
    //
    // Se queda, al revés que Accesos, y la diferencia es lo que hace cada una:
    // ésta sólo LEE. Lo único falso era una frase —«no hay eventos en este
    // período», que invita a cambiar el período cuando lo que pasa es que no
    // hay bitácora— y eso se arregla diciéndolo, como hace Integraciones con su
    // botón apagado. Ver `audit-section.tsx`.
    //
    // Lo que la alimentaría ya existe: `lead_events` registra quién cambió qué.
    // Conectarla es trabajo aparte y sigue sin decidirse.
    platformOnly: true,
  },
];

/** Where `/settings` lands: the only section every plan and every role can see. */
export const DEFAULT_SECTION_PATH = 'organization';

/**
 * Absolute href of a section, and the only way anything should link to one.
 *
 * The module renders its routes as a descendant `<Routes>` under the `/settings/*`
 * splat, and React Router resolves a relative `to` against the **whole** matched
 * URL — so `to="users"` read from `/settings/organization` navigates to
 * `/settings/organization/users` and lands on «Sección desconocida». Building the
 * path from the module registry is immune to that.
 */
export const sectionHref = (path: string): string => `${MODULES.settings.path}/${path}`;

export const sectionByPath = (path: string): SettingsSection | undefined =>
  SETTINGS_SECTIONS.find((section) => section.path === path);

export const sectionById = (id: string): SettingsSection | undefined =>
  SETTINGS_SECTIONS.find((section) => section.id === id);

/** Lo que hay que saber de quien mira para contestar si una sección se ve. */
export interface AccesoASecciones {
  can: (capability: CapabilityId) => boolean;
  /** `null` es del cliente; los dos roles nuestros ven lo `platformOnly`. */
  platformRole: PlatformRole;
  /** Los campos que esta organización tiene, ya resueltos (`useLeadFields`). */
  fields: ReadonlySet<LeadFieldId>;
  /** Los permisos efectivos de quien mira, **tal como los mandó el servidor**. */
  permisos: ReadonlySet<Permiso>;
}

/**
 * Si esta organización ve esta sección. **La única copia de la regla.**
 *
 * Los tres ejes estaban escritos tres veces —el índice, la paleta de comandos y
 * las rutas— y dos ya se habían separado, que es lo que siempre pasa con una
 * regla copiada: la paleta preguntaba por la capacidad y por el campo pero no
 * por `platformOnly`, así que ofrecía las siete secciones que son nuestras a
 * cualquier cliente que escribiera «usuarios», y el enlace llevaba a «Esto lo
 * configuramos nosotros». Las rutas de Accesos y Bitácora, por el otro lado, no
 * tenían la guardia de plataforma que sí tenían sus cinco vecinas.
 *
 * Contesta SÍ o NO y nada más. Cuál negativa corresponde —«esto lo configuramos
 * nosotros» manda a escribirnos, «está apagado para tu organización» manda a su
 * plan— es otra pregunta, y la contesta la ruta, que es la única que dibuja algo
 * cuando la respuesta es que no.
 *
 * **El permiso no ensancha nada acá**, y es la corrección del 2026-09-24:
 * ensanchaba una sección `platformOnly` a quien lo llevara, con Ajustes →
 * Usuarios como único caso, y el dueño quiso esa sección abierta para todos.
 * Ver `permiso` arriba: un permiso decide si se guarda, y solo achica lo que se
 * mira en las secciones con `soloQuienAdministra`.
 */
export function seccionVisible(section: SettingsSection, acceso: AccesoASecciones): boolean {
  if (section.capability !== undefined && !acceso.can(section.capability)) return false;
  if (section.platformOnly === true && acceso.platformRole === null) return false;
  if (section.leadField !== undefined && !acceso.fields.has(section.leadField)) return false;
  if (
    section.soloQuienAdministra === true &&
    acceso.platformRole === null &&
    (section.permiso === undefined || !acceso.permisos.has(section.permiso))
  ) {
    return false;
  }
  return true;
}

/** Las secciones que esta organización ve, en el orden del catálogo. */
export const seccionesVisibles = (acceso: AccesoASecciones): SettingsSection[] =>
  SETTINGS_SECTIONS.filter((section) => seccionVisible(section, acceso));

/** Quien mira, para la otra pregunta: no si la ve, sino si la GUARDA. */
export interface QuienConfigura {
  platformRole: PlatformRole;
  role: UserRole;
  /** Los permisos efectivos de quien mira. Mandan sobre las dos etiquetas. */
  permisos: ReadonlySet<Permiso>;
}

/**
 * Si quien mira puede **guardar** esta sección. **La única copia de la regla.**
 *
 * Hermana de `seccionVisible` y separada a propósito: ver no es guardar —saber
 * qué campos personalizados existen es parte de trabajar, definirlos no— y el
 * servidor tiene esa misma forma.
 *
 * SON TRES COMPUERTAS DISTINTAS DEL SERVIDOR, y de ahí las tres ramas:
 *
 * - **La que pregunta por el PERMISO** (`permiso`) la gatea
 *   `access.require_permission`, y va PRIMERO porque es la más específica: si
 *   una sección nombra su permiso, ése es el que decide, y las dos etiquetas
 *   dejan de opinar. Es lo que impide quedar en los dos mundos — hoy Ajustes →
 *   Usuarios, cuyo servidor pide `config.usuarios` y por lo tanto deja entrar al
 *   Administrador del cliente y deja afuera a nuestro Soporte.
 * - **Lo nuestro** (`platformWrites` / `platformOnly`) lo gatea
 *   `access.require_platform_admin`, o sea `PLATFORM_ROLES`: **los dos** roles
 *   de Ribox, super y soporte. Configurar es lo que el soporte sí hace.
 * - **La configuración del cliente** —hoy Etiquetas— la gatea
 *   `access.ADMIN_ROLES`, donde el soporte **no** está. Dibujarle «Guardar» ahí
 *   sería otra vez un control que promete lo que la API rechaza con 403, que es
 *   exactamente el defecto que `platformWrites` vino a cerrar del otro lado.
 *
 * Sin sección se contesta como configuración del cliente, que es la respuesta
 * segura: quien llama sin id no está pidiendo permiso de plataforma.
 *
 * Módulo PURO: lo usa `useAdminAccess`, que es quien tiene la sesión.
 */
export function seccionEditable(
  section: SettingsSection | undefined,
  quien: QuienConfigura,
): boolean {
  if (section?.permiso !== undefined) return quien.permisos.has(section.permiso);
  if (section?.platformWrites === true || section?.platformOnly === true) {
    return quien.platformRole !== null;
  }
  return quien.platformRole === 'super' || ROLES_QUE_ADMINISTRAN.includes(quien.role);
}
